import {useEffect, useId, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {
  fetchActiveSessionCounts,
  fetchResourceChallengeStatus,
  fetchSessionCoverage,
  fetchUpcomingSchedule,
  selectActiveSessionStatus,
  selectResourceChallengeStatus,
  selectSessionCoverage,
  selectUpcomingSchedule,
  selectUpcomingScheduleLoading,
} from '../slices/marketSlice';
import {selectSelectedResource} from '../slices/preferencesSlice';
import {selectAuthIsForecaster} from '../slices/authSlice';
import {formatDateTimeInResourceTz, getResourceTimezoneLabel, formatUtcDateTime} from '../utils/datetime';
import {deriveSessionHero} from '../utils/sessionHero';
import useMarketsOverview from '../hooks/useMarketsOverview';
import MarketsRail, {MarketsRailLabel} from './MarketsRail';

const GATE_REFRESH_INTERVAL_MS = 60 * 1000; // re-fetch open-session counts
const HERO_TICK_MS = 5 * 1000; // refresh coverage + countdown

/**
 * StatusHero — the dashboard's session focal point. Replaces SessionStatusChip.
 *
 * One open session shares a single gate closure across all its challenges, so a
 * single countdown drives the headline while each challenge is shown as a chip.
 * Mirrors the landing page's display type: light/medium two-tone headline with
 * an italic-teal emphasis word.
 */
export default function StatusHero() {
  const dispatch = useDispatch();
  const selectedResource = useSelector(selectSelectedResource);
  const activeSessionStatus = useSelector(selectActiveSessionStatus);
  const resourceChallengeStatus = useSelector(selectResourceChallengeStatus);
  const sessionCoverage = useSelector(selectSessionCoverage);
  const isForecaster = useSelector(selectAuthIsForecaster);

  const [now, setNow] = useState(() => Date.now());
  const [expandedChipId, setExpandedChipId] = useState(null);
  const toggleChip = (id) => setExpandedChipId((prev) => (prev === id ? null : id));
  const windowIdBase = useId();
  const windowLineId = (chipId) => `${windowIdBase}-window-${chipId}`;

  // Auto-collapse an expanded chip after a short while (mirrors the old chip).
  useEffect(() => {
    if (expandedChipId === null) return undefined;
    const t = setTimeout(() => setExpandedChipId(null), 10000);
    return () => clearTimeout(t);
  }, [expandedChipId]);

  // Poll open-session counts on a slower cadence; the hero text and chip pills
  // tick at HERO_TICK_MS so newly-submitted forecasts (own tab or another
  // forecaster's parallel run) reflect within a few seconds without a refresh.
  useEffect(() => {
    dispatch(fetchActiveSessionCounts());
    const refresh = setInterval(() => dispatch(fetchActiveSessionCounts()), GATE_REFRESH_INTERVAL_MS);
    const tick = setInterval(() => setNow(Date.now()), HERO_TICK_MS);
    return () => {
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, [dispatch]);

  // Load this resource's challenges for the open session.
  useEffect(() => {
    if (activeSessionStatus.sessionId && selectedResource?.id) {
      dispatch(fetchResourceChallengeStatus({
        sessionId: activeSessionStatus.sessionId,
        resourceId: selectedResource.id,
      }));
    }
  }, [dispatch, activeSessionStatus.sessionId, selectedResource?.id]);

  // The status carries the resourceId it was fetched for, so a match doubles
  // as "loaded for this resource" (distinguishes empty from still-loading).
  const challengesLoaded = !!(selectedResource && resourceChallengeStatus.resourceId === selectedResource.id);
  const challenges = challengesLoaded ? (resourceChallengeStatus.challenges || []) : [];
  const noChallenges = challengesLoaded && challenges.length === 0 && !!activeSessionStatus.sessionId;

  // With nothing to submit here, tell the user when this resource's next
  // challenges open instead (same projection the Schedule page uses).
  const upcomingSchedule = useSelector(selectUpcomingSchedule);
  const upcomingScheduleLoading = useSelector(selectUpcomingScheduleLoading);
  useEffect(() => {
    if (!noChallenges || !selectedResource?.id) return;
    dispatch(fetchUpcomingSchedule({resourceId: selectedResource.id, windowHours: 168}));
  }, [dispatch, noChallenges, selectedResource?.id]);

  // Coverage: ready/source per challenge for the open session (forecaster only).
  // Re-poll at HERO_TICK_MS so continuous-forecast coverage updates live.
  useEffect(() => {
    if (!isForecaster || !activeSessionStatus.isOpen) return;
    dispatch(fetchSessionCoverage());
    const poll = setInterval(() => dispatch(fetchSessionCoverage()), HERO_TICK_MS);
    // Don't clear coverage on unmount: it's shared Redux state the always-mounted
    // Header also reads. Clearing it here blanks the Header's "X/Y ready" dots
    // (to 0/N) until its slower 60s poll refills them. The next fetch overwrites
    // it anyway, so there's nothing stale to clear.
    return () => clearInterval(poll);
  }, [dispatch, isForecaster, activeSessionStatus.isOpen, activeSessionStatus.sessionId]);

  const hero = deriveSessionHero({
    sessionStatus: activeSessionStatus,
    challenges,
    coverage: sessionCoverage?.byChallenge || {},
    isForecaster,
    challengesLoaded,
    now,
  });

  // Earliest projected session for this resource: openings sharing a gate
  // closure are one session; surface its open time and horizon labels.
  let nextOpening = null;
  if (noChallenges) {
    const future = (upcomingSchedule?.openings || []).filter(
      (o) => o.gate_closure && new Date(o.gate_closure).getTime() > now,
    );
    if (future.length > 0) {
      const gate = future
        .map((o) => o.gate_closure)
        .sort((a, b) => new Date(a) - new Date(b))[0];
      const items = future.filter((o) => o.gate_closure === gate);
      nextOpening = {
        sessionOpen: items[0].session_open || null,
        gateClosure: gate,
        horizons: items.map((o) => o.horizon_label).filter(Boolean),
      };
    }
  }

  // Cross-market rollup for the right rail (counts + coverage are already
  // polled above; the hook only adds the session-wide challenge list).
  const marketsOverview = useMarketsOverview();

  if (!hero) return null;

  const gateText = formatDateTimeInResourceTz(activeSessionStatus.gateClosureAt);
  const tzLabel = getResourceTimezoneLabel(undefined, new Date(activeSessionStatus.gateClosureAt || Date.now()));

  const showRail = !!(marketsOverview && marketsOverview.marketCount > 1);
  const expandedChip = hero.chips.find((c) => c.id === expandedChipId && c.startDatetime && c.endDatetime);

  return (
    <div className="text-left">
      {/* Eyebrow row shared by both columns so the rail label and the session
          eyebrow sit on the same line by construction */}
      <div className="mb-1.5 flex items-center justify-between gap-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
          {hero.eyebrow}
          {gateText && ` · Gate ${hero.closed ? 'closed' : 'closes'} ${gateText} ${tzLabel}`}
        </div>
        {showRail && <MarketsRailLabel />}
      </div>

      {/* Main layout. On lg the left column (headline + chips) and the right
          column (status + markets note) share a two-row grid, so the status
          text centers against the headline and the markets note lines up with
          the chips row. */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-baseline lg:gap-x-6 lg:gap-y-0">
        <div className="min-w-0 text-2xl sm:text-3xl font-medium tracking-tight leading-tight lg:col-start-1 lg:row-start-1">
          <span className="text-gray-900 dark:text-gray-100">{hero.headline.lead}</span>
          <span className="italic font-semibold text-brand-600 dark:text-brand-400">{hero.headline.emph}</span>
          <span className="text-gray-500 dark:text-gray-400 font-normal">{hero.headline.tail}</span>
        </div>

        {/* Zero-challenge state: point at the next projected session for this
            resource instead of an empty chip row. */}
        {hero.noChallenges && (
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 lg:col-start-1 lg:row-start-2">
            {nextOpening ? (
              <>
                Next challenges open{' '}
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {formatDateTimeInResourceTz(nextOpening.sessionOpen || nextOpening.gateClosure)}{' '}
                  {getResourceTimezoneLabel(undefined, new Date(nextOpening.sessionOpen || nextOpening.gateClosure))}
                </span>
                {nextOpening.horizons.length > 0 && (
                  <span className="text-gray-400 dark:text-gray-500">
                    {' '}· {nextOpening.horizons.join(', ')}
                  </span>
                )}
              </>
            ) : upcomingScheduleLoading ? (
              'Checking the upcoming schedule…'
            ) : (
              'No challenge openings scheduled for this resource in the next 7 days.'
            )}
            <span className="text-gray-400 dark:text-gray-500"> · </span>
            <Link
              to="/schedule"
              className="text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
            >
              View schedule →
            </Link>
          </div>
        )}

        {/* Per-horizon chips. Always visible, minimal/borderless; click to reveal UTC window */}
        {hero.chips.length > 0 && (
          <div className="mt-0 flex flex-wrap items-center gap-x-1 gap-y-1.5 lg:col-start-1 lg:row-start-2">
            {hero.chips.map((c) => {
              const isExpanded = expandedChipId === c.id;
              const hasRange = !!(c.startDatetime && c.endDatetime);
              let dotClass;
              if (!hero.observer && c.ready) {
                dotClass = 'bg-green-500';
              } else if (!hero.observer && !hero.closed) {
                dotClass = 'bg-amber-500';
              } else {
                dotClass = 'bg-gray-300 dark:bg-gray-600';
              }
              let sourceGlyph = null;
              let sourceTitle = null;
              if (!hero.observer && c.ready && c.source === 'session_submission') {
                sourceGlyph = '▪'; // ▪
                sourceTitle = 'Ready — session submission';
              } else if (!hero.observer && c.ready && c.source === 'continuous_forecast') {
                sourceGlyph = '∼'; // ∼
                sourceTitle = 'Ready — continuous forecast covers this window';
              }
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => hasRange && toggleChip(c.id)}
                  aria-expanded={isExpanded}
                  aria-controls={hasRange ? windowLineId(c.id) : undefined}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2 py-2 lg:px-1.5 lg:py-1 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${hasRange ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : 'cursor-default'} ${isExpanded ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} aria-hidden="true" />
                  <span className="font-medium text-gray-700 dark:text-gray-200">{c.badge}</span>
                  {c.day && <span className="text-gray-400 dark:text-gray-500">{c.day}</span>}
                  {sourceGlyph && (
                    <span
                      title={sourceTitle}
                      aria-label={sourceTitle}
                      className="text-gray-400 dark:text-gray-500"
                    >
                      {sourceGlyph}
                    </span>
                  )}
                  {/* Inline window on lg only; phones get the line below */}
                  {hasRange && (
                    <span
                      aria-hidden={!isExpanded}
                      className={`hidden lg:inline-block overflow-hidden whitespace-nowrap transition-all duration-300 ease-out ${isExpanded ? 'max-w-[280px] opacity-100' : 'max-w-0 opacity-0'}`}
                    >
                      <span className="ml-1 border-l border-gray-200/70 dark:border-gray-600/70 pl-2 text-gray-500 dark:text-gray-400">
                        {formatUtcDateTime(c.startDatetime)} → {formatUtcDateTime(c.endDatetime)} UTC
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Phones: the tapped chip's UTC window on its own line below the
            chips (inline, an expanded chip runs past the viewport). Same
            state, so it collapses with the chip. */}
        {expandedChip && (
          <p
            id={windowLineId(expandedChip.id)}
            className="lg:hidden mt-1 font-mono text-xs text-gray-500 dark:text-gray-400"
          >
            {expandedChip.badge}{expandedChip.day && ` ${expandedChip.day}`}: {formatUtcDateTime(expandedChip.startDatetime)} → {formatUtcDateTime(expandedChip.endDatetime)} UTC
          </p>
        )}

        {showRail && <MarketsRail overview={marketsOverview} selectedResourceId={selectedResource?.id} />}
      </div>
    </div>
  );
}
